import { useEffect } from 'react';
import { Platform } from 'react-native';
import Constants from 'expo-constants';

import { useAppSelector } from '@app/store/store';
import { selectUserProfile } from '@app/store/selectors';
import { useSiteConfig } from '@app/features/store-core';

export const getSnapEngagePageModule = (pathname: string, moduleLabels: Record<string, string>): string => {
  const [, , slug, subSlug, pageSlug] = pathname.split('/');
  const lookupKey =
    slug === 'position-plan' && subSlug
      ? `${slug}/${subSlug}`
      : slug === 'store-settings' && pageSlug
        ? `${slug}/${pageSlug}`
        : slug;
  return moduleLabels[lookupKey] ?? slug ?? '';
};

export const SNAP_ENGAGE_MODULE_LABELS: Record<string, string> = {
  'position-guide': 'Dashboard Home',
  'position-plan/service': 'Position Plan (Service)',
  'position-plan/non-service': 'Position Plan (Non-Service)',
  'ops-plan': 'Ops Plan',
  'flow-chart': 'Flowchart',
  cleaning: 'Cleaning Tasks',
  sales: 'Sales & Labor Tracker',
  equipment: 'Equipment Temps',
  'speed-of-service': 'Speed of Service',
  'app-insights': 'App Insights',
  documents: 'Documents',
  calendar: 'Calendar',
  profile: 'Store Settings',
  'store-settings/general-info': 'General Info',
  'store-settings/store-hours': 'Store Hours',
  'store-settings/equipment-profile': 'Equipment Profile',
  'store-settings/team-roster': 'Team Roster',
};

export const useSnapEngageContext = () => {
  const userProfile = useAppSelector(selectUserProfile);
  const { siteConfig } = useSiteConfig();

  useEffect(() => {
    if (Platform.OS !== 'web') return;
    const widgetId = process.env.EXPO_PUBLIC_SNAPENGAGE_WIDGET_ID;
    console.log('[SnapEngage] script injection effect — widgetId:', widgetId);
    if (!widgetId) {
      console.warn('[SnapEngage] EXPO_PUBLIC_SNAPENGAGE_WIDGET_ID is not set');
      return;
    }
    if (document.querySelector(`script[src*="${widgetId}"]`)) return;

    const se = document.createElement('script');
    se.type = 'text/javascript';
    se.async = true;
    se.src = `https://storage.googleapis.com/code.snapengage.com/js/${widgetId}.js`;
    console.log('[SnapEngage] injecting script:', se.src);
    let done = false;
    se.onload = () => {
      if (done) return;
      done = true;
      console.log('[SnapEngage] script loaded — self.SnapEngage:', self.SnapEngage);
      self.SnapEngage?.hideButton();
    };
    se.onerror = e => console.error('[SnapEngage] script failed to load', e);
    const s = document.getElementsByTagName('script')[0];
    s.parentNode?.insertBefore(se, s);
  }, []);

  useEffect(() => {
    if (Platform.OS !== 'web') return;
    self.deviceType = 'Web';
    self.appType = 'opsbee-web';
    self.appVersion = Constants.expoConfig?.version ?? '';
  }, []);

  useEffect(() => {
    if (Platform.OS !== 'web') return;
    if (!userProfile) return;
    self.userLogin = userProfile.userName ?? '';
    self.userRole = String(userProfile.roles ?? '');
    self.isLoggedIn = true;

    console.log('[SnapEngage] user effect — self.SnapEngage available:', !!self.SnapEngage, '| email:', userProfile.contactEmail);
    if (self.SnapEngage) {
      if (userProfile.contactEmail) self.SnapEngage.setUserEmail(userProfile.contactEmail, true);
      const fullName = [userProfile.firstName, userProfile.lastName].filter(Boolean).join(' ');
      if (fullName) self.SnapEngage.setUserName(fullName);
    }
  }, [userProfile?.userId]);

  useEffect(() => {
    if (Platform.OS !== 'web') return;
    if (!siteConfig?.id) return;
    const location = [siteConfig.address?.city, siteConfig.address?.state].filter(Boolean).join(', ');
    self.brandName = siteConfig.companyInfo?.brand?.name ?? '';
    self.chainName = siteConfig.companyInfo?.chainName ?? '';
    self.storeId = siteConfig.stopId ?? '';
    self.storeName = siteConfig.name ?? '';
    self.storeMarket = siteConfig.configSettings?.marketTitle ?? '';
    self.storeLocation = location;
    self.storeCountry = siteConfig.address?.country ?? '';
    self.storePlan = siteConfig.config?.smsPlan?.toString() ?? '';

    self.SnapEngage?.setCustomField('StoreNumber', siteConfig.stopId ?? '');
  }, [siteConfig?.id]);

  useEffect(() => {
    if (Platform.OS !== 'web') return;
    const updatePageContext = () => {
      const pathname = self.location.pathname;
      self.pageModule = getSnapEngagePageModule(pathname, SNAP_ENGAGE_MODULE_LABELS);
      self.pageUrl = pathname;
    };

    updatePageContext();
    self.addEventListener('popstate', updatePageContext);
    return () => self.removeEventListener('popstate', updatePageContext);
  }, []);
};
